import React, { useContext, useState, useEffect, useRef } from "react";
import { AuthContext } from "../context/AuthContext";
import { faCheckCircle, faExclamationCircle, faRightToBracket } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  displayMessage
} from "../utils/functions";
import Spinner from '../utils/spinner';
import InlineVerificationCode from './VerifyCodePanel';
import Config from '../utils/Config';
import useTitle from '../hooks/useTitle';
import { toast } from "react-toastify";
import "./Login.css"; // reuse styling from login/register
import "./Account.css";

const Account = ({ isAdmin }) => {
  const { user, updateProfile } = useContext(AuthContext); // 🔥 updateProfile assumed
  const [formData, setFormData] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    useMFA: user?.useMFA || false,
    timeZone: user?.timeZone || "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messageClass, setMessageClass] = useState("hidden");
  const [loginLogs, setLoginLogs] = useState([]);
  const [timeZone, setTimeZone] = useState("");
  const [timeZones, setTimeZones] = useState([]);
  const [bottomButtonsVisible, setBottomButtonsVisible] = useState(false);
  const messageRef = useRef(null);
  const formRef = useRef(null);
  const bottomButtonsRef = useRef(null);
  const [securityMeta, setSecurityMeta] = useState({
    lastVerifiedAt: null,
    passwordChangedAt: null,
    session: { ip: null, location: null }
  });
  const [mfaPending, setMfaPending] = useState(false);
  const [mfaModalOpen, setMfaModalOpen] = useState(false);
  const [mfaChallengeId, setMfaChallengeId] = useState(null);
  const [mfaExpiresAt, setMfaExpiresAt] = useState(null);
  const [mfaAction, setMfaAction] = useState(null); // 'enable' | 'disable'
  const [pendingMessage, setPendingMessage] = useState("");
  const [pendingStyle, setPendingStyle] = useState("warning");
  const [adminLevel, setAdminLevel] = useState("warning");

  useTitle('My Account');

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const formatIP = (ip) => {
    if (!ip) return "—";
    return ip?.replace(/^::ffff:/, '') || "—";
  };

  const submitViaCode = () => {
    if (!formRef.current) {
      console.error("⚠️ formRef is null—check the ref attribute.");
      return;
    }

    // Prefer modern API
    if (typeof formRef.current.requestSubmit === "function") {
      formRef.current.requestSubmit();
    } else {
      // Fallback for older browsers: fire a synthetic submit event
      const evt = new Event("submit", { bubbles: true, cancelable: true });
      formRef.current.dispatchEvent(evt);
    }
  };

  const onClose = async (e) => {
    setFormData(prev => ({ ...prev, useMFA: mfaAction === 'enable' ? false : true }));
    setMfaModalOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const rawResponse = await fetch(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.USER_UPDATE_ACCOUNT}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify(formData),
      });

      const response = await rawResponse.json();

      if (response?.success) {
        toast.success("Profile updated successfully");

        // Clear visual inline message (if visible from a previous error)
        if (messageRef.current) {
          messageRef.current.style.display = "none";
          setMessageClass("hidden");
        }
      } else {
        toast.error(response.message || "❌ Update failed.");
        displayMessage(messageRef, response.message || "❌ Update failed.", "warning", setMessageClass);
      }
    } catch (err) {
      toast.error("❌ Server error.");
      displayMessage(messageRef, "❌ Server error.", "warning", setMessageClass);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchLoginLogs = async () => {
      try {
        const res = await fetch(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.USER_GET_LOGIN_ACTIVITY}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem("authToken")}`
          },
        });

        if (!res.ok) throw new Error("Failed to fetch login logs");
        const data = await res.json();
        setLoginLogs(data.logs || []);
      } catch (err) {
        console.error("Login activity fetch error:", err);
      }
    };

    fetchLoginLogs();
  }, []);

  useEffect(() => {
    const fetchTimeZones = async () => {
      try {
        const res = await fetch(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.USER_TIMEZONES}`);
        const data = await res.json();
        setTimeZones(data);

        const detectedZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const match = data.find(tz => tz.value === detectedZone);
        if (match && !timeZone) {
          setTimeZone(detectedZone);
        }
      } catch (err) {
        console.error("❌ Error fetching timezones:", err);
      }
    };

    fetchTimeZones();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setBottomButtonsVisible(entry.isIntersecting);
      },
      { threshold: 0.15 } // Trigger when at least 10% of the row is visible
    );

    if (bottomButtonsRef.current) {
      observer.observe(bottomButtonsRef.current);
    }

    return () => {
      if (bottomButtonsRef.current) {
        observer.unobserve(bottomButtonsRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const fetchSecurityMeta = async () => {
      try {
        const res = await fetch(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.USER_SECURITY_META}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        });
        const data = await res.json();
        setSecurityMeta(data);
      } catch (err) {
        console.error("❌ Failed to load security meta:", err);
      }
    };

    fetchSecurityMeta();
  }, []);

  const toggleMFA = async (next) => {
    // next === true → enable, false → disable
    if (!user?.id) {
      toast.error('No user id available');
      return;
    }

    setFormData(prev => ({ ...prev, useMFA: next })); // reflect click
    setMfaPending(true);

    const intent = next ? 'enable' : 'disable';
    const url = `${Config.API.API_BASE_URL}${
      // USER_TOGGLE_MFA should be '/user-toggle-mfa/:id'
      Config.ENDPOINTS.USER_TOGGLE_MFA.replace(':id', user._id)
      }`;

    try {
      const r = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify({ step: 'init', intent }),
      });

      const data = await r.json().catch(() => ({}));

      setPendingMessage("✅ Verification code sent. Please check email.");

      if (!r.ok) {
        if (r.status === 429) {
          const ra = r.headers.get('Retry-After');
          if (ra) toast.warn(`❌ Too many requests. Try again in ${ra}s.`);
        }
        throw new Error(data?.message || 'Could not start MFA challenge.');
      }

      // server may no-op if already in desired state
      if (data.already) {
        toast.info(`MFA is already ${next ? 'enabled' : 'disabled'}.`);
        setFormData(prev => ({ ...prev, useMFA: next }));
        return;
      }

      if (!data.challengeId || !data.codeExpiresAt) {
        throw new Error('Malformed MFA init response.');
      }

      // Open the OTP modal
      setMfaAction(intent);
      setMfaChallengeId(data.challengeId);
      setMfaExpiresAt(data.codeExpiresAt);
      setMfaModalOpen(true);
    } catch (err) {
      // rollback UI if init failed
      setFormData(prev => ({ ...prev, useMFA: !next }));
      toast.error(err.message || 'MFA update failed');
    } finally {
      setMfaPending(false);
    }
  };

  return (
    <div className="login-container login-container-dashboard">
      <div className="login-box" id="account-details-box">
        <h2 className="dashboard-title">
          <span className="titleIcon"><FontAwesomeIcon icon="user" /></span>&nbsp;&nbsp;My Account Details
        </h2>

        <div ref={messageRef} className={messageClass} />

        <form ref={formRef} onSubmit={handleSubmit}>
          <label>
            <div className="account-fields-row">
              <div className="account-field">
                {/* <label>First Name:</label> */}
                <input
                  type="text"
                  placeholder="First Name"
                  value={formData.firstName}
                  onChange={(e) =>
                    setFormData({ ...formData, firstName: e.target.value })
                  }
                />
              </div>

              <div className="account-field">
                {/* <label>Last Name:</label> */}
                <input
                  type="text"
                  placeholder="Last Name"
                  value={formData.lastName}
                  onChange={(e) =>
                    setFormData({ ...formData, lastName: e.target.value })
                  }
                />
              </div>
            </div>
          </label>

          <label>
            {/* <label>Email:</label> */}
            <input
              type="email"
              placeholder="Email"
              readOnly
              name="email"
              value={formData.email}
              onChange={handleChange}
              disabled
            />
          </label>

          <label>
            <p>
              {/* <label>Time Zone:</label> */}
              <select
                name="timeZone"
                className="selectCtrl"
                value={formData.timeZone}
                onChange={(e) => setFormData(prev => ({ ...prev, timeZone: e.target.value }))}
              >
                <option value="">Select Time Zone</option>
                {timeZones.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    (UTC {tz.offset}) {tz.name}
                  </option>
                ))}
              </select>
            </p>
          </label>

          <label disabled>
            <div className="mfa-toggle">
              <input
                id="useMFA"
                type="checkbox"
                checked={formData.useMFA}
                disabled={mfaPending || isAdmin}
                onChange={(e) => toggleMFA(e.target.checked)}
              />
              <label htmlFor="useMFA" className={isAdmin ? "mfa-admin-text" : ""}>Enable Email Multifactor Authentication</label>
            </div>
            <p className={isAdmin ? "mfa-description-hidden" : "mfa-description"}>
              ** Adds email-based 2FA to protect your account. You'll need a code from your inbox every time you sign in. <strong>Recommended</strong>.
            </p>
            <p className={isAdmin ? "mfa-description" : "mfa-description-hidden"}>
              ** REQUIRED BY POLICY FOR ADMIN
            </p>
          </label>

          <div className="account-security-meta">
            <p className="mfa-verified">
              Last Verified: <span>{securityMeta.lastVerifiedAt ? new Date(securityMeta.lastVerifiedAt).toLocaleString() : "—"}</span>
            </p>
            <p className="last-changed">
              Password changed: <span>{securityMeta.passwordChangedAt ? new Date(securityMeta.passwordChangedAt).toLocaleString() : "—"}</span>
            </p>
            <p className="session-info">
              Current Session:{" "}
              <span className="session-info-right"><i>
                {securityMeta.session?.ip ? (
                  <>
                    {securityMeta.session.ip.replace(/^::ffff:/, "")}
                    <div className="securityMetaLocation">({securityMeta.session.location || "Unknown"})</div>
                  </>
                ) : (
                  "—"
                )}</i>
              </span>
            </p>
          </div>

          <div className="login-activity-wrapper">
            <h3>
              <span className="titleIcon"><FontAwesomeIcon icon={faRightToBracket} className="activity-icon" /></span>
              &nbsp;Login Activity
            </h3>

            <table className="login-table rtable">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>IP Address</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {loginLogs.map((log, i) => {
                  const ok = String(log.status).toLowerCase() === "success";
                  return (
                    <tr key={i} className={ok ? "ok" : "failed"}>
                      <td data-label="Date">{new Date(log.timestamp).toLocaleString()}</td>
                      <td data-label="IP Address" className="ip">
                        {formatIP(log.ipAddress)}
                      </td>
                      <td data-label="Status" className="status">
                        <FontAwesomeIcon
                          icon={ok ? faCheckCircle : faExclamationCircle}
                          className={`status-icon ${ok ? "success-login" : "failed-login"}`}
                        />
                        <span className="status-text status-text-account">{String(log.status).toUpperCase()}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

          </div>

          <button type="submit" className="login-button" disabled={isSubmitting} ref={bottomButtonsRef}>
            {isSubmitting ? "Saving..." : "Save Changes"}
          </button>
        </form>
      </div>

      <Spinner show={isSubmitting} />

      {!bottomButtonsVisible && (
        <div className="button-row">
          {/* 🚀 Sticky SAVE/UPDATE button */}
          <button
            type="button"                // MUST be button, not submit, or you’ll get nested-form quirks
            disabled={isSubmitting}
            className="sticky-save-btn btn-update-account"
            onClick={submitViaCode}
          >
            Save
          </button>
        </div>
      )}

      {mfaModalOpen && (
        <div className="modal-overlay">
          <div className="modal modal-account">
            { }
            <InlineVerificationCode
              onSubmit={async (code) => {
                const url = mfaAction === 'enable'
                  ? Config.ENDPOINTS.USER_MFA_ENABLE_COMPLETE
                  : Config.ENDPOINTS.USER_MFA_DISABLE_COMPLETE;

                const r = await fetch(`${Config.API.API_BASE_URL}${url}`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                  },
                  body: JSON.stringify({ challengeId: mfaChallengeId, code }),
                });
                const data = await r.json().catch(() => ({}));

                if (r.ok) {
                  toast.success(mfaAction === 'enable' ? 'MFA enabled' : 'MFA disabled');
                  setMfaModalOpen(false);
                  // Persist the final state in formData (already set), optionally refresh security meta
                } else {
                  displayMessage(messageRef, data?.message || 'Invalid code', "warning", setMessageClass);
                  toast.error(data?.message || 'Invalid code');
                }
              }}
              onResend={async () => {
                const url = mfaAction === 'enable'
                  ? Config.ENDPOINTS.USER_MFA_RESEND_ENABLE
                  : Config.ENDPOINTS.USER_MFA_RESEND_DISABLE;

                console.log(url);
              }}
              messageClass={messageClass}
              messageRef={messageRef}
              emailInputRef={{ current: null }}
              codeExpiresAt={mfaExpiresAt}
              setExpiresAt={setMfaExpiresAt}
              setPendingMessage={setPendingMessage}
              setMessageClass={setMessageClass}
              setPendingStyle={setPendingStyle}
              email={formData.email}
              isLockedOut={false}
              parent="mfa"
              mfaAction={mfaAction}
              mfaChallengeId={mfaChallengeId}
              pendingMessage={pendingMessage}
              onClose={onClose}
            />
          </div>
        </div>
      )}

    </div>
  );
};

export default Account;